import type { DealerImportResult, DealerImportRowInput, PreferredContactMethod, SessionUser } from '@motoveo/shared';
import { PREFERRED_CONTACT_METHODS } from '@motoveo/shared';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';

/** Minimal quoted-CSV line splitter (handles "a,b" quoted fields + escaped ""). */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (inQ) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else inQ = false;
      } else cur += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === ',') {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

const HEADER_ALIASES: Record<keyof DealerImportRowInput, string[]> = {
  dealership: ['dealership', 'dealer', 'business', 'business_name', 'store'],
  state: ['state', 'st'],
  contact_name: ['contact_name', 'contact', 'contactname', 'rep', 'salesperson'],
  phone_e164: ['phone', 'phone_e164', 'mobile', 'cell', 'tel', 'telephone'],
  email: ['email', 'e-mail', 'mail'],
  role_title: ['role', 'role_title', 'title', 'position'],
  relationship_owner_name: ['relationship_owner', 'relationship_owner_name', 'owner', 'founder', 'rel_owner'],
  preferred_contact_method: ['preferred_contact_method', 'preferred_contact', 'contact_method'],
  notes: ['notes', 'note', 'comment', 'comments'],
  // M15/M5 — identity disambiguators (two same-name dealers in one state are distinct businesses).
  city: ['city', 'town', 'locality'],
  external_ref: ['external_ref', 'external_id', 'externalref', 'crm_id', 'ref', 'dealer_ref'],
  // refined model — Porsche franchise store (true/yes/1). Only franchise dealers are blasted.
  franchise: ['franchise', 'is_franchise', 'franchise_store', 'porsche_franchise'],
};

/** Parse a CSV truthy/falsy cell into a boolean, or undefined when blank/unrecognized. */
function parseBoolCell(raw: string): boolean | undefined {
  const v = raw.trim().toLowerCase();
  if (['true', 'yes', 'y', '1'].includes(v)) return true;
  if (['false', 'no', 'n', '0'].includes(v)) return false;
  return undefined;
}

/** Normalize a raw phone to E.164 (US default) or return null when it can't be made valid. */
function normalizePhone(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const cleaned = trimmed.replace(/[^\d+]/g, '');
  if (/^\+[1-9]\d{6,14}$/.test(cleaned)) return cleaned;
  const digits = cleaned.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

interface ParsedRow {
  line: number;
  row?: DealerImportRowInput;
  error?: string;
}

function parseCsv(text: string): { rows: ParsedRow[]; headerFound: boolean } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { rows: [], headerFound: false };
  const header = splitCsvLine(lines[0]!).map((h) => h.toLowerCase());
  const colOf = (key: keyof DealerImportRowInput): number =>
    header.findIndex((h) => HEADER_ALIASES[key].includes(h));
  const idx = {
    dealership: colOf('dealership'),
    state: colOf('state'),
    contact_name: colOf('contact_name'),
    phone_e164: colOf('phone_e164'),
    email: colOf('email'),
    role_title: colOf('role_title'),
    relationship_owner_name: colOf('relationship_owner_name'),
    preferred_contact_method: colOf('preferred_contact_method'),
    notes: colOf('notes'),
    city: colOf('city'),
    external_ref: colOf('external_ref'),
    franchise: colOf('franchise'),
  };
  const headerFound = idx.dealership >= 0 && idx.state >= 0;
  const rows: ParsedRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitCsvLine(lines[i]!);
    const get = (n: number): string => (n >= 0 && n < cells.length ? cells[n]!.trim() : '');
    const dealership = get(idx.dealership);
    const stateRaw = get(idx.state).toUpperCase();
    if (!dealership || stateRaw.length !== 2) {
      rows.push({ line: i + 1, error: `missing dealership or 2-letter state (got "${dealership}", "${stateRaw}")` });
      continue;
    }
    const phoneRaw = get(idx.phone_e164);
    const phone = phoneRaw ? normalizePhone(phoneRaw) : null;
    const emailRaw = get(idx.email);
    const method = get(idx.preferred_contact_method).toLowerCase();
    const row: DealerImportRowInput = {
      dealership,
      state: stateRaw,
      ...(get(idx.contact_name) ? { contact_name: get(idx.contact_name) } : {}),
      ...(phone ? { phone_e164: phone } : {}),
      ...(emailRaw ? { email: emailRaw } : {}),
      ...(get(idx.role_title) ? { role_title: get(idx.role_title) } : {}),
      ...(get(idx.relationship_owner_name) ? { relationship_owner_name: get(idx.relationship_owner_name) } : {}),
      ...(PREFERRED_CONTACT_METHODS.includes(method as PreferredContactMethod)
        ? { preferred_contact_method: method as PreferredContactMethod }
        : {}),
      ...(get(idx.notes) ? { notes: get(idx.notes) } : {}),
      ...(get(idx.city) ? { city: get(idx.city) } : {}),
      ...(get(idx.external_ref) ? { external_ref: get(idx.external_ref) } : {}),
      ...(parseBoolCell(get(idx.franchise)) !== undefined ? { franchise: parseBoolCell(get(idx.franchise)) } : {}),
    };
    const note = phoneRaw && !phone ? 'phone dropped (not valid E.164)' : undefined;
    rows.push({ line: i + 1, row, error: note });
  }
  return { rows, headerFound };
}

const SAMPLE =
  'dealership,state,contact_name,phone,email,role,relationship_owner,notes\n' +
  'Wedge Motors,FL,Dana Buyer,305-555-0100,dana@wedge.example,Acquisitions,Alex,Top GT buyer\n' +
  'Coast Porsche,CA,,,,,Sam,Prospect only — no contact yet';

/** Bulk-import a founder's dealer contact list → prospects + contacts. Dry-run preview, then commit. */
export function DealerImportPage({ user }: { user: SessionUser }) {
  const [text, setText] = useState('');
  const parsed = useMemo(() => parseCsv(text), [text]);
  const validRows = parsed.rows.filter((r) => r.row).map((r) => r.row!);
  const localErrors = parsed.rows.filter((r) => !r.row);

  const run = useMutation({
    mutationFn: (dry_run: boolean) => api.dealers.import({ rows: validRows, dry_run }),
  });
  const result: DealerImportResult | undefined = run.data;

  return (
    <Layout user={user}>
      <p>
        <Link to="/dealers" className="muted">
          ← Dealers
        </Link>
      </p>
      <h2>Import dealer contacts (CSV)</h2>
      <p className="muted">
        Paste a founder’s dealer list. Header row required with at least <code>dealership</code> and <code>state</code>; optional{' '}
        <code>contact_name, phone, email, role, relationship_owner, preferred_contact_method, notes, city, external_ref, franchise</code>. Creates{' '}
        <strong>prospect</strong> dealers + contacts, idempotent on dealer identity (name + state + <code>city</code>/<code>external_ref</code>)
        and phone/email. A <code>franchise</code> column (true/yes/1) marks a Porsche FRANCHISE store — only active franchise dealers receive
        blasts. Two same-name dealers in one state are DISTINCT businesses — a row that can’t be separated from an existing dealer
        is held as <strong>needs_review</strong> (never silently merged). No SMS consent is captured on import.
      </p>

      <div className="card form">
        <label>
          CSV
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} placeholder={SAMPLE} spellCheck={false} />
        </label>
        <div className="row-between">
          <button type="button" className="plain" onClick={() => setText(SAMPLE)}>
            Load sample
          </button>
          <span className="muted">
            {validRows.length} valid row{validRows.length === 1 ? '' : 's'}
            {localErrors.length ? ` · ${localErrors.length} unparseable` : ''}
            {parsed.rows.length && !parsed.headerFound ? ' · ⚠ header must include dealership + state' : ''}
          </span>
        </div>
        <div className="row-between">
          <div>
            <button type="button" disabled={run.isPending || validRows.length === 0} onClick={() => run.mutate(true)}>
              {run.isPending ? 'Working…' : 'Preview (dry run)'}
            </button>{' '}
            <button
              type="button"
              disabled={run.isPending || validRows.length === 0}
              onClick={() => {
                if (window.confirm(`Import ${validRows.length} rows for real?`)) run.mutate(false);
              }}
            >
              Import for real
            </button>
          </div>
          {run.isError ? <span className="error">{(run.error as Error).message}</span> : null}
        </div>
      </div>

      {localErrors.length > 0 ? (
        <div className="card">
          <h3>Unparseable rows ({localErrors.length})</h3>
          <ul className="plain muted">
            {localErrors.map((r) => (
              <li key={r.line}>
                line {r.line}: {r.error}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {result ? (
        <div className="card">
          <h3>
            {result.dry_run ? 'Dry-run preview' : 'Import result'} — {result.total} rows
          </h3>
          <p>
            <span className="pill conf-high">created {result.created}</span> <span className="pill">updated {result.updated}</span>{' '}
            <span className="pill">skipped {result.skipped}</span>{' '}
            {result.needs_review > 0 ? <span className="pill conf-medium">needs review {result.needs_review}</span> : null}{' '}
            {result.errors > 0 ? <span className="pill conf-low">errors {result.errors}</span> : null}
            {result.dry_run ? <span className="muted"> — nothing written yet. Click “Import for real” to commit.</span> : null}
          </p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Dealership</th>
                  <th>Outcome</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r) => (
                  <tr key={r.index}>
                    <td className="muted">{r.index + 1}</td>
                    <td>
                      {r.dealer_id ? <Link to={`/dealers/${r.dealer_id}`}>{r.dealership}</Link> : r.dealership}
                    </td>
                    <td>
                      <span
                        className={`pill ${
                          r.outcome === 'error' ? 'conf-low' : r.outcome === 'needs_review' ? 'conf-medium' : r.outcome === 'created' ? 'conf-high' : ''
                        }`}
                      >
                        {r.outcome}
                      </span>
                    </td>
                    <td className="muted">{r.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </Layout>
  );
}
